import { useState, useEffect } from 'react';
import { usePlatform } from '../context/PlatformContext';
import { useParams, useNavigate } from 'react-router-dom';
import { getPublicPaymentRequest, payPaymentRequest, createGuestPayIntent } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

function GuestCheckout({ amountUsd, onSuccess }) {
  const stripe   = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [error, setError]   = useState('');

  async function handlePay() {
    if (!stripe || !elements) return;
    setPaying(true); setError('');
    try {
      const { error: stripeError } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required'
      });
      if (stripeError) {
        setError(stripeError.message);
      } else {
        onSuccess();
      }
    } catch (err) {
      setError('Payment failed. Please try again.');
    } finally { setPaying(false); }
  }

  return (
    <div>
      <PaymentElement/>
      {error && <div style={s.error}>{error}</div>}
      <button style={{...s.btn, marginTop:16}} onClick={handlePay} disabled={paying || !stripe}>
        {paying ? 'Processing...' : `Pay $${amountUsd}`}
      </button>
    </div>
  );
}

export default function PayPage() {
  useEffect(() => {
    document.title = 'Thunderbolt — Pay';
    return () => { document.title = 'Thunderbolt'; };
  }, []);

  const platform  = usePlatform();
  const { requestId } = useParams();
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const [request, setRequest]           = useState(null);
  const [loading, setLoading]           = useState(true);
  const [paying, setPaying]             = useState(false);
  const [error, setError]               = useState('');
  const [success, setSuccess]           = useState(null);
  const [guestIntent, setGuestIntent]   = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [guestLoading, setGuestLoading] = useState(false);
  const [payMode, setPayMode]           = useState(null);
  const [reported, setReported]         = useState(() => !!localStorage.getItem(`reported_${requestId}`));
  const [reporting, setReporting]       = useState(false);

  async function handleReport() {
    if (reported || reporting) return;
    if (!window.confirm('Report this payment request as spam?')) return;
    setReporting(true);
    try {
      await fetch(`${process.env.REACT_APP_API_URL || ''}/payments/report/${requestId}`, { method: 'POST' });
      setReported(true);
      localStorage.setItem(`reported_${requestId}`, '1');
    } catch (e) { /* silent */ }
    setReporting(false);
  }

  useEffect(() => {
    getPublicPaymentRequest(requestId)
      .then(r => setRequest(r.data))
      .catch(() => setError('Payment request not found or expired'))
      .finally(() => setLoading(false));
  }, [requestId]);

  async function handleAccountPay() {
    setPaying(true); setError('');
    try {
      const res = await payPaymentRequest(requestId);
      setSuccess(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Payment failed');
    } finally { setPaying(false); }
  }

  async function handleGuestPay() {
    setGuestLoading(true); setError('');
    try {
      const res = await createGuestPayIntent(requestId);
      const { clientSecret, publishableKey } = res.data;
      const stripe = await loadStripe(publishableKey);
      setStripePromise(Promise.resolve(stripe));
      setGuestIntent(res.data);
      setPayMode('guest');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to initialize payment');
    } finally { setGuestLoading(false); }
  }

  if (loading) return (
    <div style={s.center}><div style={s.spinner}/></div>
  );

  if (success) return (
    <div style={s.page}>
      <div style={s.successWrap}>
        <div style={s.successIcon}>✓</div>
        <div style={s.successTitle}>Payment sent!</div>
        <div style={s.successAmount}>${success.amountUsd || request?.amountUsd}</div>
        <div style={s.successTo}>to {success.merchantName || request?.merchantName}</div>
        {success.description && <div style={s.successDesc}>{success.description}</div>}
        <button style={s.btn} onClick={() => window.close()}>Done</button>
        <button style={s.altBtn} onClick={() => navigate('/')}>Join Thunderbolt free</button>
      </div>
    </div>
  );

  return (
    <div style={s.page}>
      <div style={s.header}>
        <img src="/tbold-logo-white.png" alt="Thunderbolt" style={{height:40, width:'auto', objectFit:'contain'}}/>
      </div>

      <div style={s.body}>
        {error && !request ? (
          <div style={s.errorCard}>
            <div style={s.errorIcon}>✕</div>
            <div style={s.errorTitle}>{error}</div>
          </div>
        ) : request ? (
          <>
            {/* Merchant card */}
            <div style={s.requestCard}>
              <div style={{marginBottom:8, display:'flex', alignItems:'center', justifyContent:'center', gap:8, flexWrap:'wrap'}}>
                {request.invoiceNumber && <span style={{fontSize:12, color:'#AEAEB2', display:'block', marginBottom:2}}>Invoice #{request.invoiceNumber}</span>}
                <span style={s.merchantName}>{request.merchantName}</span>
                {request.description && <span style={s.description}>· {request.description}</span>}
              </div>
              {request.lineItems && request.lineItems.length > 0 && (
                <div style={{background:'#F5F5F7', borderRadius:10, padding:'10px 14px', margin:'10px 0', textAlign:'left'}}>
                  {request.lineItems.map((item, i) => (
                    <div key={i} style={{display:'flex', justifyContent:'space-between', fontSize:13, padding:'3px 0', borderBottom: i < request.lineItems.length-1 ? '1px solid #E5E5EA' : 'none'}}>
                      <span style={{color:'#3D3D3D'}}>{item.description}</span>
                      <span style={{fontWeight:600}}>${parseFloat(item.price).toFixed(2)}</span>
                    </div>
                  ))}

                </div>
              )}
              <div style={s.amount}>${request.amountUsd}</div>
              <div style={s.amountLabel}>USD</div>
            </div>

            {/* Fee summary */}
            <div style={s.summaryCard}>
              <div style={s.summaryRow}>
                <span style={s.summaryLabel}>{`Service fee (${platform.feePct}%)`}</span>
                <span style={s.summaryValue}>
                  ${(request.amountCents * (platform.feeBps / 10000) / 100).toFixed(2)}
                </span>
              </div>
              <div style={s.divider}/>
              <div style={s.summaryRow}>
                <span style={{...s.summaryLabel, fontWeight:600, color:'#1D1D1F'}}>Total charged</span>
                <span style={{...s.summaryValue, fontWeight:600, color:'#1D1D1F'}}>${request.amountUsd}</span>
              </div>
            </div>

            {/* Guest card payment */}
            {payMode === 'guest' && guestIntent && stripePromise ? (
              <div style={s.stripeCard}>
                <div style={s.stripeTitle}>Pay with card</div>
                <Elements
                  stripe={stripePromise}
                  options={{
                    clientSecret: guestIntent.clientSecret,
                    appearance: { theme: 'stripe' }
                  }}>
                  <GuestCheckout
                    amountUsd={request.amountUsd}
                    onSuccess={() => setSuccess({
                      amountUsd: request.amountUsd,
                      merchantName: request.merchantName
                    })}
                  />
                </Elements>
                <button style={{...s.altBtn, marginTop:12}}
                  onClick={() => setPayMode(null)}>
                  Back
                </button>
              </div>

            ) : user && payMode !== 'guest' ? (
              /* Logged in — pay with account */
              <>
                <div style={s.payingAs}>
                  Paying as <strong>{user.name}</strong>
                </div>
                {error && <div style={s.error}>{error}</div>}
                <button style={s.btn} onClick={handleAccountPay} disabled={paying}>
                  {paying ? 'Processing...' : `Pay $${request.amountUsd} with Thunderbolt`}
                </button>
                <button style={s.altBtn} onClick={handleGuestPay} disabled={guestLoading}>
                  {guestLoading ? 'Loading...' : 'Pay with card instead'}
                </button>
                <div style={s.powered}>
                  {`Powered by Thunderbolt · ${platform.feePct}% fee`}
                </div>
              </>

            ) : (
              /* Not logged in — show options */
              <div style={s.optionsCard}>
                <div style={s.optionsTitle}>How would you like to pay?</div>

                <button style={s.btn} onClick={handleGuestPay} disabled={guestLoading}>
                  {guestLoading ? 'Loading...' : '💳 Pay with card'}
                </button>

                <div style={s.orDivider}>
                  <div style={s.orLine}/>
                  <span style={s.orText}>or</span>
                  <div style={s.orLine}/>
                </div>

                <button style={s.altBtn} onClick={() => navigate(`/?redirect=/pay/${requestId}`)}>
                  ⚡ Pay with Thunderbolt account
                </button>

                <div style={s.joinText}>
                  No account?{' '}
                  <span style={s.joinLink} onClick={() => navigate(`/?redirect=/pay/${requestId}`)}>
                    Join free — save in Bitcoin automatically
                  </span>
                </div>

                {error && <div style={s.error}>{error}</div>}

                <div style={s.powered}>
                  {`Powered by Thunderbolt · ${platform.feePct}% fee`}
                </div>
              </div>
            )}
          </>
        ) : null}
      </div>

      {/* Report footer */}
      {request && !success && (
        <div style={s.reportBar}>
          <span style={s.reportFrom}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight:4}}>
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            Request from <strong style={{marginLeft:3}}>{request.merchantName}</strong>
          </span>
          <button style={{...s.reportBtn, ...(reported ? s.reportedBtn : {})}} onClick={handleReport} disabled={reported || reporting}>
            {reported ? (
              <>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{marginRight:4}}>
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Reported
              </>
            ) : (
              <>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight:4}}>
                  <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>
                  <line x1="4" y1="22" x2="4" y2="15"/>
                </svg>
                {reporting ? 'Reporting...' : 'Report as Spam'}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

const s = {
  page:          {minHeight:'100vh', background:'#F5F5F7', maxWidth:480, margin:'0 auto'},
  center:        {display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh'},
  spinner:       {width:32, height:32, borderRadius:'50%', border:'3px solid #F0F0F0', borderTopColor:'#007AFF'},
  header:        {background:'#007AFF', padding:'20px 20px 24px', display:'flex', alignItems:'center', justifyContent:'center'},
  body:          {padding:'16px 16px 40px'},
  requestCard:   {background:'#fff', borderRadius:24, padding:'24px', textAlign:'center', marginBottom:12, boxShadow:'0 2px 20px rgba(0,0,0,0.06)'},
  merchantAvatar:{width:48, height:48, borderRadius:'50%', background:'#EBF4FF', color:'#007AFF', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, fontWeight:600, flexShrink:0},
  merchantName:  {fontSize:15, fontWeight:500, color:'#1D1D1F'},
  description:   {fontSize:15, fontWeight:500, color:'#1D1D1F'},
  amount:        {fontSize:48, fontWeight:700, letterSpacing:-2, lineHeight:1},
  amountLabel:   {fontSize:13, color:'#AEAEB2', marginTop:4},
  summaryCard:   {background:'#fff', borderRadius:16, padding:16, marginBottom:16, boxShadow:'0 2px 20px rgba(0,0,0,0.06)'},
  summaryRow:    {display:'flex', justifyContent:'space-between', alignItems:'center', padding:'5px 0'},
  summaryLabel:  {fontSize:14, color:'#6E6E73'},
  summaryValue:  {fontSize:14, color:'#6E6E73'},
  divider:       {height:1, background:'#F5F5F7', margin:'10px 0'},
  optionsCard:   {background:'#fff', borderRadius:20, padding:24, textAlign:'center', marginBottom:12, boxShadow:'0 2px 20px rgba(0,0,0,0.06)'},
  optionsTitle:  {fontSize:17, fontWeight:600, color:'#1D1D1F', marginBottom:20},
  stripeCard:    {background:'#fff', borderRadius:20, padding:20, marginBottom:12, boxShadow:'0 2px 20px rgba(0,0,0,0.06)'},
  stripeTitle:   {fontSize:15, fontWeight:600, marginBottom:16},
  orDivider:     {display:'flex', alignItems:'center', gap:10, margin:'12px 0'},
  orLine:        {flex:1, height:1, background:'#E5E5E7'},
  orText:        {fontSize:12, color:'#AEAEB2', fontWeight:500},
  payingAs:      {fontSize:13, color:'#6E6E73', textAlign:'center', marginBottom:12},
  joinText:      {fontSize:13, color:'#6E6E73', marginTop:12},
  joinLink:      {color:'#007AFF', cursor:'pointer', fontWeight:500},
  error:         {background:'#FFF0EF', border:'1px solid #FFD0CE', borderRadius:10, padding:'10px 14px', fontSize:13, color:'#FF3B30', marginBottom:12, marginTop:8},
  btn:           {width:'100%', padding:16, background:'#007AFF', color:'#fff', border:'none', borderRadius:14, fontSize:16, fontWeight:600, letterSpacing:-0.3, marginBottom:8, cursor:'pointer'},
  altBtn:        {width:'100%', padding:14, background:'transparent', color:'#007AFF', border:'1.5px solid #007AFF', borderRadius:14, fontSize:15, fontWeight:500, marginBottom:8, cursor:'pointer'},
  powered:       {textAlign:'center', fontSize:12, color:'#AEAEB2', marginTop:8},
  errorCard:     {background:'#fff', borderRadius:20, padding:48, textAlign:'center', boxShadow:'0 2px 20px rgba(0,0,0,0.06)'},
  errorIcon:     {fontSize:32, color:'#FF3B30', marginBottom:12},
  errorTitle:    {fontSize:16, color:'#6E6E73'},
  successWrap:   {display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'100vh', padding:24, gap:8, textAlign:'center'},
  successIcon:   {width:64, height:64, borderRadius:'50%', background:'#E6FAF5', color:'#00C48C', display:'flex', alignItems:'center', justifyContent:'center', fontSize:28, marginBottom:8},
  successTitle:  {fontSize:18, fontWeight:600, color:'#1D1D1F'},
  successAmount: {fontSize:48, fontWeight:700, letterSpacing:-2, color:'#007AFF'},
  successTo:     {fontSize:14, color:'#6E6E73'},
  successDesc:   {fontSize:13, color:'#AEAEB2'},
  reportBar:     {position:'fixed', bottom:0, left:0, right:0, maxWidth:480, margin:'0 auto', background:'#fff', borderTop:'1px solid #F0F0F0', padding:'12px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', zIndex:50},
  reportFrom:    {fontSize:12, color:'#6E6E73', display:'flex', alignItems:'center'},
  reportBtn:     {display:'flex', alignItems:'center', fontSize:12, fontWeight:500, color:'#FF3B30', background:'#FFF0EF', border:'1px solid #FFD0CE', borderRadius:8, padding:'6px 12px', cursor:'pointer', whiteSpace:'nowrap'},
  reportedBtn:   {color:'#00C48C', background:'#E6FAF5', border:'1px solid #B7F0DC', cursor:'default'}
};
